import { useState, useEffect } from "react";
import { FileText } from "lucide-react";

import { FileComponentProps } from "../../types";
import { base64ToBuffer, getFileSizeFromBase64 } from "../../utils";
import { FileThumbnail } from "../FileThumbnail";
import { FilePreviewWrapper } from "../FilePreviewWrapper";

import { PdfControls } from "./PdfControls";
import { PdfViewer } from "./PdfViewer";

export const PdfDisplay = ({ data, detectedFile, isViewing, setIsViewing }: FileComponentProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<string>("");

  useEffect(() => {
    const createBlobUrl = () => {
      try {
        let cleanedData = data;

        if (cleanedData.startsWith("[") && cleanedData.endsWith("]")) {
          try {
            const parsed = JSON.parse(cleanedData);
            if (Array.isArray(parsed) && parsed.length > 0) {
              cleanedData = parsed[0];
            }
          } catch {
            cleanedData = cleanedData.slice(1, -1);
          }
        }

        cleanedData = cleanedData.replace(/["'\s\r\n]/g, "");

        const sizeInBytes = getFileSizeFromBase64(cleanedData);
        setFileSize(sizeInBytes);

        const bytes = base64ToBuffer(cleanedData);

        const blob = new Blob([new Uint8Array(bytes)], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(blob);

        setBlobUrl(url);
        setError(null);

        return () => {
          if (url) {
            URL.revokeObjectURL(url);
          }
        };
      } catch (e) {
        setError(`Failed to process PDF data: ${e instanceof Error ? e.message : "Unknown error"}`);
      }
    };

    return createBlobUrl();
  }, [data]);

  if (!detectedFile.mimeType) {
    return (
      <div className="flex items-center justify-center p-4">
        <span className="text-gray-500">Unable to display PDF</span>
      </div>
    );
  }

  const handleOpenInNewTab = () => {
    if (!blobUrl) {
      return;
    }
    window.open(blobUrl, "_blank");
  };

  return (
    <div className="group relative">
      <div className="cursor-pointer rounded p-2 transition-colors hover:bg-gray-100">
        <FilePreviewWrapper
          isViewing={isViewing}
          setIsViewing={setIsViewing}
          contentClassName="w-auto max-w-[800px] p-3"
          trigger={
            <div>
              <FileThumbnail
                icon={<FileText className="h-6 w-6 text-gray-600" />}
                title="PDF Document"
                subtitle={fileSize || "PDF"}
              />
            </div>
          }
        >
          <div className="space-y-1">
            <PdfControls blobUrl={blobUrl} onOpenInNewTab={handleOpenInNewTab} />
            <PdfViewer blobUrl={blobUrl} error={error} />
          </div>
        </FilePreviewWrapper>
      </div>
    </div>
  );
};
